/**
 * @module experience/shop/cart-review
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/*tslint:disable:max-file-line-count*/
/*tslint:disable:max-line-length*/
/* tslint:disable:cyclomatic-complexity*/

import { EventEmitter, Injectable } from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { BillingTypeEnum } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import {
  Cart,
  CartItem,
  CartLine,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { FIELD_VALIDATIONS_CONSTANTS } from '@support/shared/services/common-api/common-api.constants';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import {
  CartReview,
  CartLineItem,
  CartNonLineItem,
  ReplacementItem,
  InventoryCheckMap,
  RisInventoryDetails,
} from '@experience/shop/cart/interfaces/cart-review.interface';
import { DEVICE_TYPE } from '@experience/shop/cart/services/cart-review.constants';
import {
  CART_REVIEW_CONST,
  VALIDATE_MODAL_CONSTANTS,
} from '@experience/shop/cart/constants/cart-review.constants';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { CART_LANDING_CONSTANTS } from '@experience/shop/cart/constants/cart-landing.constants';

@Injectable()
export class CartReviewService {
  public formChange: EventEmitter<boolean>;
  /**
   * Holds the value of current time
   */
  public lastTime: number;
  public nonDuplicateItems: CartNonLineItem[];

  /**
   * Check if the flow is remorse exchange
   */
  private isExchange: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private formCreationService: FormCreationService,
    private stateStoreUtils: StateStoreUtilsService,
    private commonUtilService: CommonUtilService,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService
  ) {
    this.formChange = new EventEmitter();
    this.lastTime = new Date().getTime();
  }

  /**
   * This Method is used to get Modal Data
   * @param {Cart} cart
   * @returns {CartReview}
   */
  public getValidateModalData(cart: Cart): CartReview {
    const orderType = this.objectService.get(cart, 'orderType');
    this.isExchange = orderType === CART_REVIEW_CONST.EXCHANGE;
    if (this.isExchange) {
      return {
        cartId: this.objectService.get(cart, 'cartId'),
        lineItems: this.buildLineItemsDataForExchange(this.objectService.get(cart, 'lines')),
        nonLineItems: this.buildNonLineItemsDataForExchange(
          this.objectService.get(cart, 'replacementItem'),
          false
        ),
        nonLineDeviceItems: this.buildNonLineItemsDataForExchange(
          this.objectService.get(cart, 'replacementItem'),
          true
        ),
      };
    }
    return {
      cartId: this.objectService.get(cart, 'cartId'),
      lineItems: this.buildLineItemsData(this.objectService.get(cart, 'lines')),
      nonLineItems: this.buildNonLineItemsData(this.objectService.get(cart, 'accessories')),
      nonLineDeviceItems: this.buildDeviceCartItems(this.objectService.get(cart, 'devices')),
    };
  }

  public setLineItems(lineId: string, lineItems: CartLineItem): void {
    this.stateStoreUtils.setLineItems(lineId, lineItems);
  }

  public setNonLineItems(offerId: string, nonLineItem: CartNonLineItem): void {
    this.stateStoreUtils.setNonLineItems(offerId, nonLineItem);
  }

  /**
   * This Method is used to check Validate Modal Data Errors
   * @param {cartReview} validateModalData
   * @returns boolean
   */
  public checkCartItemsError(validateModalData: CartReview): boolean {
    let isValidateError = false;
    if (validateModalData.lineItems && validateModalData.lineItems.length) {
      isValidateError = this.validateError(validateModalData.lineItems);
    }
    if (
      validateModalData.nonLineDeviceItems &&
      validateModalData.nonLineDeviceItems.length &&
      !isValidateError
    ) {
      isValidateError = this.validateError(validateModalData.nonLineDeviceItems);
    }
    return isValidateError;
  }

  /**
   * This Method is used to check validation Errors in Cart Items
   * @param {cartLineItem[] | CartNonLineItem[]}  cartItems
   * @returns boolean
   */
  private validateError(cartItems: CartLineItem[] | CartNonLineItem[]): boolean {
    let isError = false;
    for (const Item of cartItems) {
      isError =
        Item.inventoryError ||
        (Item.hasOwnProperty('isSimValid') && !Item.isSimValid) ||
        (Item.imeiError && Item.imeiError !== '')
          ? true
          : false;
      if (isError) {
        break;
      }
    }
    return isError;
  }

  /**
   * This Method is used to store remorse exchange item
   * @param {string} offerId
   * @param {ReplacementItem} replacementItem
   */
  public setExcahangeItem(offerId: string, replacementItem: ReplacementItem): void {
    this.stateStoreUtils.setExcahangeItem(offerId, replacementItem);
  }

  /**
   * Get the remorse exchange item validation objects
   * @param {string} offerId
   * @return ReplacementItem
   */
  public getExcahangeItem(offerId: string): ReplacementItem {
    return this.stateStoreUtils.getExcahangeItem(offerId);
  }

  /**
   * Get the Non Line item validation objects
   */
  public getNonLineItems(offerId: string): CartNonLineItem {
    return this.stateStoreUtils.getNonLineItems(offerId);
  }

  /**
   * This Method is used to create Cart Validation Form
   * @param {CartReview} validateModalData
   * @param {isUpgrade} boolean
   * @param {isRemorseExchange} boolean
   * @returns {FormGroup}
   */
  public buildCartValidationForm(
    validateModalData: CartReview,
    isUpgrade?: boolean,
    isRemorseExchange?: boolean
  ): UntypedFormGroup {
    if (!isUpgrade) {
      validateModalData.lineItems = validateModalData.lineItems.filter((line: CartLineItem) => {
        const lineStatus: string = this.commonUtilService.toUpperCase(line.lineStatus);
        // @TODO adding case WARNING for QC616071
        // @TODO confirm with PO with status as PENDING
        switch (lineStatus) {
          case 'ADDED':
            return true;
          case 'ACTIVE':
            return isRemorseExchange;
          case 'CHANGED':
            return true;
          case 'WARNING':
            return true;
          default:
            return false;
        }
      });
    }
    return this.formBuilder.group({
      lineItems: this.buildLineItemsValidateFormArray(
        this.objectService.get(validateModalData, 'lineItems'),
        isUpgrade,
        isRemorseExchange
      ),
      nonLineItems: this.buildAccessoriesValidateFormArray(
        this.objectService.get(validateModalData, 'nonLineItems')
      ),
      nonLineDeviceItems: this.buildDeviceValidateFormArray(
        this.objectService.get(validateModalData, 'nonLineDeviceItems')
      ),
    });
  }

  /**
   * Retrieves a form validation group
   * @returns FormGroup
   */
  public buildCartFormGroup(accessories: CartItem[]): UntypedFormGroup {
    let form: UntypedFormGroup;
    const accessoryLength = accessories.length;
    for (let i: number = 0; i < accessoryLength; i++) {
      form = this.formBuilder.group({
        quantity: [accessories[i].quantity],
      });
    }
    return form;
  }

  public buildDealerSkuForm(): UntypedFormGroup {
    return this.formBuilder.group({
      dealer: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.DEALER_CODE,
        '',
        true
      ),
      sku: this.formCreationService.getFormControl(FIELD_VALIDATIONS_CONSTANTS.SKU, '', true),
    });
  }

  /**
   * This Method makes the token call if the cart is empty on load.
   * @param {checkCart} validateModalData
   * @returns {void}
   */
  public checkCart(): void {
    if (!this.commonUtilService.isNotEmpty(this.stateStoreUtils.getCartId())) {
      this.cartSupportService.setDefaultCartState().subscribe((res: BrowseEntry) => {});
    }
  }

  /**
   * Returns true if customer is in prepaid
   * @returns {boolean}
   * @memberof CartReviewService
   */
  public prepaidBillingType(): boolean {
    const customerConfigData = this.stateStoreUtils.getCustomerConfigData();
    const customerBillingType = this.objectService.get(customerConfigData, 'billingType');
    return customerBillingType !== BillingTypeEnum.POSTPAID;
  }

  /**
   * creates a form group of the quantity dropdown for each accessory
   * @params cartResponse
   * @returns form group for accessories form
   */
  public buildAccessoriesForm(cartResponse: Cart): UntypedFormGroup {
    const formGrp = this.formBuilder.group({});
    if (cartResponse.accessories && Array.isArray(cartResponse.accessories)) {
      const accessoryLength = cartResponse.accessories.length;
      for (let i = 0; i < accessoryLength; i++) {
        const controlName = 'quantity'.concat(i.toString());
        formGrp.addControl(controlName, new UntypedFormControl());
      }
    } else if (cartResponse.replacementItem && Array.isArray(cartResponse.replacementItem)) {
      const accessoryLength = cartResponse.replacementItem.length;
      for (let i = 0; i < accessoryLength; i++) {
        const controlName = 'quantity'.concat(i.toString());
        formGrp.addControl(controlName, new UntypedFormControl());
      }
    }
    return formGrp;
  }

  /**
   * Create a form for promo code
   * @returns FormGroup
   */
  public buildPromoCodeForm(): UntypedFormGroup {
    return this.formBuilder.group({
      promotion: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.PROMO_CODE,
        '',
        true
      ),
    });
  }
  /**
   * Checks whether the input is from a keyboard or a scanner
   * @param event : Holds the value of the Event
   * @return string
   */
  public detectDeviceOnScan(): string {
    const currentTime = new Date().getTime();
    const delay = 50;
    let deviceType: string;
    if (currentTime - this.lastTime < delay) {
      deviceType = DEVICE_TYPE.SCANNER;
    } else {
      deviceType = DEVICE_TYPE.KEYBOARD;
    }
    // Initialize the timer for the first character
    this.lastTime = new Date().getTime();
    return deviceType;
  }

  /**
   * This Method is used to check whether item is added from BYOS flow or not
   * @param {line} CartLine
   * @returns boolean
   */
  private isByosLineItem(line: CartLine): boolean {
    return this.objectService.get(line, 'simNumber') && !this.objectService.get(line, 'device');
  }
  /**
   * This Method is used to build LineItems in Cart
   * @param {Line[]} lines
   * @returns {CartLineItem[]}
   */
  // tslint:disable-next-line:cyclomatic-complexity
  public buildLineItemsData(lines: CartLine[]): CartLineItem[] {
    let lineItems = [];
    const linesLength = lines.length;
    const isUpgradeFlow = this.stateStoreUtils.getUpgradeFlow();
    if (isUpgradeFlow) {
      lineItems = this.getLineItemDataForUpgradeFlow(lines);
    } else {
      for (let i = 0; i < linesLength; i++) {
        // E2E FIX - QC613147 - Validation modal was incorrect incase we add first item from BYOS flow and second from BYOD flow.
        const isByosLineItem = this.isByosLineItem(lines[i]);
        if (isByosLineItem) {
          continue;
        }
        // E2E - Fix ends
        const deviceSku = this.objectService.get(lines, `${i}.device.sku`);
        const simNumber =
          this.objectService.get(lines, `${i}.simKit.simNumber`) ||
          this.objectService.get(lines, `${i}.simNumber`);
        const validatedLineItem = this.stateStoreUtils.getLineItems(
          this.objectService.get(lines, i.toString().concat('.id'))
        );
        const repSku = this.objectService.get(validatedLineItem, 'device.repEnteredSku');
        lineItems[i] = {
          lineLabel: lines[i].lineName
            ? this.objectService.get(lines, i.toString().concat('.lineName'))
            : this.cartSupportService.lineTextFormatter(i + 1),
          lineId: this.objectService.get(lines, i.toString().concat('.id')),
          title: lines[i].device ? lines[i].device.offerName : '',
          subTitle: '',
          validated: this.objectService.get(validatedLineItem, 'validated'),
          lineStatus: this.objectService.get(lines, i.toString().concat('.lineStatus')),
          planCartItemId: this.objectService.get(lines, `${i}.plan.0.id`),
          uskuSimCardId: this.objectService.get(lines, `${i}.simCard.id`),
        };
        const line = lines[i];
        const productSubType = this.objectService.get(line, 'device.productSubType.0');
        if (productSubType === CART_REVIEW_CONST.SIMCARD) {
          if (this.isSimCard(lines[i])) {
            lineItems[i].simKit = {
              defaultSku: deviceSku, // since sim sku comes in device sku
              repEnteredSku:
                deviceSku && simNumber
                  ? deviceSku
                  : this.objectService.get(validatedLineItem, 'simKit.repEnteredSku'),
              sim: simNumber, // sim# comes in simKit object
              id: this.objectService.get(lines, `${i}.device.id`),
            };
          }
          if (this.objectService.get(lineItems, `${i}.simKit.sim`)) {
            this.updateCheckInventoryMap(
              i,
              VALIDATE_MODAL_CONSTANTS.LINEITEMS,
              VALIDATE_MODAL_CONSTANTS.SIM,
              this.objectService.get(lineItems, `${i}.simKit.sim`),
              this.objectService.get(lineItems, `${i}.simKit.id`)
            );
          }
        } else {
          lineItems[i].device = {
            offerId: this.objectService.get(lines, i.toString().concat('.device.offerId')),
            defaultSku: this.objectService.get(lines, i.toString().concat('.device.sku')),
            upc: this.objectService.get(lines, `${i}.device.upc`),
            repEnteredSku:
              validatedLineItem &&
              (repSku === deviceSku || repSku === this.objectService.get(lines, `${i}.device.upc`))
                ? this.objectService.get(validatedLineItem, 'device.repEnteredSku')
                : '',
            imei: validatedLineItem ? this.objectService.get(validatedLineItem, 'device.imei') : '',
            tacCode: this.objectService.get(lines, i.toString().concat('.device.tacCode')),
            sim: this.objectService.get(lines, `${i}.simNumber`),
            id: this.objectService.get(lines, `${i}.plan.0.id`),
            plan: this.objectService.get(lines, `${i}.plan`),
            deviceCartItemId: this.objectService.get(lines, `${i}.device.id`),
          };
          if (!!this.objectService.get(lineItems, `${i}.repEnteredSku`)) {
            this.updateCheckInventoryMap(
              i,
              VALIDATE_MODAL_CONSTANTS.LINEITEMS,
              VALIDATE_MODAL_CONSTANTS.SKU,
              lineItems[i].repEnteredSku,
              lineItems[i].device.id
            );
          }
        }
      }
    }
    return lineItems;
  }
  /**
   * method to check if replacement item is available
   * @param cartResponse
   * @return boolean
   */
  public isReplacementItemAvailable(cartResponse: Cart): boolean {
    let hasReturningLineItem: boolean = false;
    let hasOneReplacementLineItem: boolean = false;
    if (cartResponse && cartResponse.lines && cartResponse.lines.length > 0) {
      let moveforward = true;
      cartResponse.lines.forEach((lineItem: CartLine) => {
        if (lineItem.returningItem && lineItem.returningItem.id && moveforward) {
          hasReturningLineItem = true;
          if (lineItem.replacementItem && lineItem.replacementItem.id) {
            hasOneReplacementLineItem = true;
          } else {
            moveforward = false;
          }
        }
      });
    }
    const hasOneStandAloneReplacementItem = !!this.objectService.get(
      cartResponse,
      `replacementItem.length`
    );
    if (hasReturningLineItem) {
      return hasOneReplacementLineItem;
    } else if (hasOneStandAloneReplacementItem) {
      return true;
    } else {
      return false;
    }
  }
  /**
   * method to check if replacement item is available
   * @param cartResponse
   * @return boolean
   */
  public getKeyForInventoryMap(index: number, itemType: string, cartItemId: string): string {
    let key: string;
    const value = index.toString();
    if (value && itemType) {
      key = `${itemType}${cartItemId}`;
    }
    return key;
  }
  /**
   * method to get inventory Map
   * @param cartResponse
   * @return boolean
   */
  public getInventoryMap(): InventoryCheckMap {
    return this.stateStoreUtils.getInventoryCheckMap();
  }

  /**
   * method to get inventory Map
   * @param cartResponse
   * @return boolean
   */
  public setInventoryMap(inventoryCheckMap: InventoryCheckMap): void {
    this.stateStoreUtils.setInventoryCheckMap(inventoryCheckMap);
  }

  public updateCheckInventoryMap(
    index: number,
    itemType: string,
    methodType: string,
    value: string,
    cartItemId: string
  ): void {
    const inventoryCheckMap = this.getInventoryMap() || {};
    const key = this.getKeyForInventoryMap(index, itemType, cartItemId);
    const type =
      methodType === VALIDATE_MODAL_CONSTANTS.SKU
        ? VALIDATE_MODAL_CONSTANTS.SKU
        : VALIDATE_MODAL_CONSTANTS.SERIAL_NUMBER;
    if (this.objectService.get(inventoryCheckMap, `${key}`)) {
      const currentSku: string = this.objectService.get(inventoryCheckMap, `${key}.sku`);
      const currentSerialNumber = this.objectService.get(inventoryCheckMap, `${key}.serialNumber`);
      switch (methodType) {
        case VALIDATE_MODAL_CONSTANTS.IMEI:
        case VALIDATE_MODAL_CONSTANTS.SIM:
          if (currentSerialNumber !== value) {
            inventoryCheckMap[key].serialNumber = value;
          }
          break;
        case VALIDATE_MODAL_CONSTANTS.SKU:
          if (currentSku !== value) {
            inventoryCheckMap[key].sku = value;
          }
          break;
        default:
          return;
      }
    } else {
      inventoryCheckMap[key] =
        type === VALIDATE_MODAL_CONSTANTS.SERIAL_NUMBER ? { serialNumber: value } : { sku: value };
    }
    this.setInventoryMap(inventoryCheckMap);
  }

  /**
   * Checks whether the scanned sku is already mapped to respective serial number
   * @param Item : returning Item
   * @return boolean
   */
  public checkItemInInventoryMapping(key: string, methodType: string): boolean {
    const inventoryCheckMap: InventoryCheckMap = this.getInventoryMap();
    let mappedItem: string;
    if (methodType === VALIDATE_MODAL_CONSTANTS.SERIAL_NUMBER) {
      mappedItem = this.objectService.get(inventoryCheckMap, `${key}.serialNumber`) || '';
    } else {
      mappedItem = this.objectService.get(inventoryCheckMap, `${key}.sku`);
    }
    return this.commonUtilService.isNotEmpty(mappedItem);
  }

  /**
   * Method to build CartLineItem
   * @param {CartLine[]} lines
   * @returns {CartLineItem[]}
   */
  private buildLineItemsDataForExchange(lines: CartLine[]): CartLineItem[] {
    const cartLineItems: CartLineItem[] = [];

    lines.forEach((lineItem: CartLine, index: number) => {
      if (lineItem.replacementItem) {
        const cartLineItem: CartLineItem = {
          lineId: this.objectService.get(lineItem, 'id'),
          lineStatus: this.objectService.get(lineItem, 'lineStatus'),
          title: this.objectService.get(lineItem, 'replacementItem.offerName'),
          replacementItem: {
            id: this.objectService.get(lineItem, 'replacementItem.id'),
            imei: this.objectService.get(lineItem, 'replacementItem.imei'),
            offerId: this.objectService.get(lineItem, 'replacementItem.offerId'),
            tacCode: this.objectService.get(lineItem, 'replacementItem.tacCode'),
          },
        };
        const cartReplacementLineSku = this.objectService.get(lineItem, 'replacementItem.sku');
        const manualEnteredSku = this.stateStoreUtils.getManualEnteredSku();
        if (manualEnteredSku && cartReplacementLineSku === manualEnteredSku) {
          cartLineItem.replacementItem.repEnteredSku = manualEnteredSku;
          cartLineItem.replacementItem.defaultSku = manualEnteredSku;
        } else if (cartReplacementLineSku) {
          cartLineItem.replacementItem.repEnteredSku = cartReplacementLineSku;
          cartLineItem.replacementItem.defaultSku = cartReplacementLineSku;
        }
        cartLineItems.push(cartLineItem);
        if (!!this.objectService.get(cartLineItem, 'repEnteredSku')) {
          this.updateCheckInventoryMap(
            index,
            VALIDATE_MODAL_CONSTANTS.LINEITEMS,
            VALIDATE_MODAL_CONSTANTS.SKU,
            cartLineItem.replacementItem.repEnteredSku,
            cartLineItem.replacementItem.id
          );
        }
      }
    });

    return cartLineItems;
  }

  /**
   * This Method is used to build LineItems in Cart for upgrade flow
   * @param {Line[]} lines
   * @returns {CartLineItem[]}
   */
  private getLineItemDataForUpgradeFlow(lines: CartLine[]): CartLineItem[] {
    const lineItems = [];
    const linesLength = lines.length;
    for (let i = 0; i < linesLength; i++) {
      const isUpgradeLine =
        this.objectService.get(lines[i], 'device.actionCode') === CART_REVIEW_CONST.ADD;
      if (isUpgradeLine) {
        const validatedLineItem = this.stateStoreUtils.getLineItems(
          this.objectService.get(lines, i.toString().concat('.id'))
        );
        lineItems.push({
          lineLabel: lines[i].lineName
            ? this.objectService.get(lines, i.toString().concat('.lineName'))
            : this.cartSupportService.lineTextFormatter(i + 1),
          lineId: this.objectService.get(lines, i.toString().concat('.id')),
          title: lines[i].device ? lines[i].device.offerName : '',
          subTitle: '',
          validated: this.objectService.get(validatedLineItem, 'validated'),
          lineStatus: this.objectService.get(lines, i.toString().concat('.lineStatus')),
          device: {
            offerId: this.objectService.get(lines, i.toString().concat('.device.offerId')),
            defaultSku: this.objectService.get(lines, i.toString().concat('.device.sku')),
            repEnteredSku: validatedLineItem
              ? this.objectService.get(validatedLineItem, 'device.repEnteredSku')
              : '',
            imei: validatedLineItem ? this.objectService.get(validatedLineItem, 'device.imei') : '',
            sim: this.objectService.get(lines, `${i}.simNumber`),
            id: this.objectService.get(lines, `${i}.plan.0.id`),
            plan: this.objectService.get(lines, `${i}.plan`),
            tacCode: this.objectService.get(lines, `${i}.device.tacCode`),
            deviceCartItemId: this.objectService.get(lines, `${i}.device.id`),
            upc: this.objectService.get(lines, `${i}.device.upc`),
          },
        });
        break;
      }
    }
    return lineItems;
  }

  /**
   * This Method is used to build validation for each Line Item
   * @param {CartLineItem} lineItem
   * @param {boolean} isUpgrade
   * @param {boolean} isRemorseExchange
   * @returns {FormGroup}
   */
  public buildLineItemValidateForm(
    lineItem: CartLineItem,
    isUpgrade?: boolean,
    isRemorseExchange?: boolean
  ): UntypedFormGroup {
    const validatedLineItem = this.stateStoreUtils.getLineItems(lineItem.lineId);
    let deviceValidationRequired: boolean;
    let deviceRepEnteredSku: string;
    let simRepEnteredSku: string;
    let lineItemForm;
    let sku: string;
    let imei: string;

    if (isRemorseExchange) {
      simRepEnteredSku = this.objectService.get(
        validatedLineItem,
        'replacementItem.defaultSku',
        ''
      );
      imei = this.objectService.get(validatedLineItem, 'replacementItem.imei', '');

      lineItemForm = this.formBuilder.group({
        imei: this.formCreationService.getFormControl(FIELD_VALIDATIONS_CONSTANTS.IMEI, imei, true),
        sku: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.SKU,
          simRepEnteredSku,
          true
        ),
      });
    } else {
      deviceValidationRequired = !!this.objectService.get(validatedLineItem, 'device.offerId');
      deviceRepEnteredSku = this.objectService.get(validatedLineItem, 'device.repEnteredSku', '');
      simRepEnteredSku = this.objectService.get(validatedLineItem, 'simKit.repEnteredSku', '');
      imei = this.objectService.get(validatedLineItem, 'device.imei', '');
      sku = deviceRepEnteredSku || simRepEnteredSku;

      lineItemForm = this.formBuilder.group({
        imei: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.IMEI,
          imei,
          deviceValidationRequired
        ),
        sku: this.formCreationService.getFormControl(FIELD_VALIDATIONS_CONSTANTS.SKU, sku, true),
      });
    }

    if (!isUpgrade && !isRemorseExchange) {
      const simFormControl = this.formCreationService.getAbstractFormControl(
        FIELD_VALIDATIONS_CONSTANTS.SIM,
        validatedLineItem
          ? this.objectService.get(validatedLineItem, 'simKit.sim') ||
              this.objectService.get(validatedLineItem, 'device.sim')
          : '',
        true
      );
      lineItemForm.addControl('sim', <AbstractControl>simFormControl);
    }
    return lineItemForm;
  }

  /**
   *
   * @param {CartLineItem[]} lineItems
   * @param {boolean} isUpgrade
   * @param {boolean} isRemorseExchange
   * @returns {FormArray}
   */
  public buildLineItemsValidateFormArray(
    lineItems: CartLineItem[],
    isUpgrade?: boolean,
    isRemorseExchange?: boolean
  ): UntypedFormArray {
    const deviceValidateForm = this.formBuilder.array([]);
    lineItems.forEach((lineItem: CartLineItem) => {
      if (
        this.objectService.get(lineItem, 'device.defaultSku') ||
        this.objectService.get(lineItem, 'simKit.defaultSku') ||
        this.objectService.get(lineItem, 'replacementItem.defaultSku')
      ) {
        this.stateStoreUtils.setLineItems(this.objectService.get(lineItem, 'lineId'), lineItem);
        deviceValidateForm.push(
          this.buildLineItemValidateForm(lineItem, isUpgrade, isRemorseExchange)
        );
      }
    });

    return deviceValidateForm;
  }

  /**
   * Function to check if line device type is simcard
   * @param line cartline
   */
  public isSimCard(line: CartLine): boolean {
    return this.objectService.get(line, 'device.productSubType.0') === CART_REVIEW_CONST.SIMCARD;
  }

  /**
   * This Method is used to create Cart Validation Form
   * @param {CartNonLineItem[]} nonLineItems
   * @returns {FormArray}
   */
  public buildAccessoriesValidateFormArray(nonLineItems: CartNonLineItem[]): UntypedFormArray {
    const accessoryValidateForm = this.formBuilder.array([]);
    const skuList = [];
    const itemId = [];
    this.nonDuplicateItems = nonLineItems.filter((item: CartNonLineItem) => {
      if (itemId.indexOf(item.id) === -1) {
        itemId.push(item.id);
        if (skuList.indexOf(item.defaultSku) === -1) {
          skuList.push(item.defaultSku);
        }
        return item;
      }
    });
    this.nonDuplicateItems.forEach((nonLineItem: CartNonLineItem) => {
      accessoryValidateForm.push(this.buildAccessoryValidateForm(nonLineItem));
    });
    return accessoryValidateForm;
  }

  /**
   * This Method is used to create Cart Validation Form
   */
  public buildDeviceValidateFormArray(nonLineItems: CartNonLineItem[]): UntypedFormArray {
    const deviceValidateFormArray = this.formBuilder.array([]);
    nonLineItems.forEach((nonLineItem: CartNonLineItem) => {
      deviceValidateFormArray.push(this.buildDeviceValidateForm(nonLineItem));
    });

    return deviceValidateFormArray;
  }

  /**
   * Used to get return exchange transaction type
   * @returns boolean
   */
  public getTransactionType(): boolean {
    const items = this.stateStoreUtils.getSelectedProductForReturn();
    let transactionType = '';
    if (items && items.type) {
      transactionType = items.type.toUpperCase();
    }
    if (transactionType === CART_REVIEW_CONST.EXCHANGE) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * This method is used to build non-lineItems in cart
   * @param {Accessory[]} accessories
   * @returns {CartNonLineItem[]}
   */
  private buildNonLineItemsData(accessories: CartItem[]): CartNonLineItem[] {
    const nonLineItems = [];
    if (accessories && accessories.length > 0) {
      accessories.forEach((value: CartItem, index: number) => {
        const validatedNonLineItem = this.stateStoreUtils.getNonLineItems(
          this.objectService.get(accessories, `${index}.offerId`)
        );
        nonLineItems.push({
          id: this.objectService.get(accessories, `${index}.id`),
          offerId: this.objectService.get(accessories, `${index}.offerId`),
          title: this.objectService.get(accessories, `${index}.offerName`),
          defaultSku: this.objectService.get(accessories, `${index}.sku`),
          upc: this.objectService.get(accessories, `${index}.upc`),
          repEnteredSku: validatedNonLineItem
            ? this.objectService.get(validatedNonLineItem, 'repEnteredSku')
            : '',
          productSubtype: this.objectService.get(value, 'productSubType.0')
            ? this.objectService.get(value, 'productSubType.0').toLowerCase()
            : '',
        });
      });
    }
    return nonLineItems;
  }

  /**
   * This method is used to build non-lineItems in cart
   * @param {Accessory[]} accessories
   * @returns {CartNonLineItem[]}
   */
  private buildDeviceCartItems(devices: CartItem[]): CartNonLineItem[] {
    const nonLineItems = [];
    if (devices && devices.length > 0) {
      const deviceLength = devices.length;
      for (let i = 0; i < deviceLength; i++) {
        const validatedNonLineItem = this.stateStoreUtils.getNonLineItems(
          this.objectService.get(devices, (+i).toString().concat('.id'))
        );
        const deviceSku = this.objectService.get(devices, `${i}.sku`);
        const simNumber =
          this.objectService.get(devices, `${i}.simKit.simNumber`) ||
          this.objectService.get(devices, `${i}.simNumber`);
        let simKitDetails = {};
        if (
          this.objectService.get(devices, `${i}.productSubType.0`) === CART_REVIEW_CONST.SIMCARD
        ) {
          simKitDetails = {
            defaultSku: deviceSku, // since sim sku comes in device sku
            repEnteredSku: this.objectService.get(validatedNonLineItem, 'simKit.repEnteredSku'),
            sim: simNumber, // sim# comes in simKit object
            id: this.objectService.get(devices, `${i}.id`),
          };
        }
        nonLineItems[i] = {
          simKit: simKitDetails,
          id: this.objectService.get(devices, (+i).toString().concat('.id')),
          offerId: this.objectService.get(devices, i.toString().concat('.offerId')),
          title: this.objectService.get(devices, (+i).toString().concat('.offerName')),
          defaultSku: this.objectService.get(devices, (+i).toString().concat('.sku')),
          productSubtype: this.objectService.get(
            devices,
            (+i).toString().concat('.productSubType.0')
          ),
          tacCode: this.objectService.get(devices, i.toString().concat('.tacCode')),
          upc: this.objectService.get(devices, `${i}.upc`),
          imei: validatedNonLineItem ? this.objectService.get(validatedNonLineItem, 'imei') : '',
          repEnteredSku: validatedNonLineItem
            ? this.objectService.get(validatedNonLineItem, 'repEnteredSku')
            : '',
        };
        if (!!this.objectService.get(nonLineItems, `${i}.repEnteredSku`)) {
          this.updateCheckInventoryMap(
            i,
            VALIDATE_MODAL_CONSTANTS.NON_LINE_DEVICES_ITEMS,
            VALIDATE_MODAL_CONSTANTS.SKU,
            nonLineItems[i].repEnteredSku,
            nonLineItems[i].id
          );
        }
      }
    }
    return nonLineItems;
  }

  /**
   * This method is used to build exchange Item in cart
   * @param {CartItem} replacementItem
   * @returns {ReplacementItem}
   */
  private buildExchangeDeviceCartItem(replacementItem: CartItem): ReplacementItem {
    let replacedItem = {};
    if (replacementItem) {
      const item: ReplacementItem = this.getExcahangeItem(replacementItem.offerId);
      replacedItem = {
        id: this.objectService.get(replacementItem, 'id'),
        offerId: this.objectService.get(replacementItem, 'offerId'),
        title: this.objectService.get(replacementItem, 'offerName'),
        defaultSku: this.objectService.get(replacementItem, 'sku'),
        imei: this.objectService.get(item, 'imei') ? this.objectService.get(item, 'imei') : '',
        repEnteredSku: this.objectService.get(item, 'sku')
          ? this.objectService.get(item, 'sku')
          : '',
      };
    }
    return replacedItem;
  }

  /**
   * This Method is used to build validation for each non Line Item
   * @param {CartNonLineItem} nonLineItem
   * @returns {FormGroup}
   */
  private buildAccessoryValidateForm(nonLineItem: CartNonLineItem): UntypedFormGroup {
    const validatedNonLineItem = this.stateStoreUtils.getNonLineItems(nonLineItem.offerId);
    const productSubType = this.objectService.get(nonLineItem, 'productSubtype');
    const productSubTypeUCase = productSubType && productSubType.toUpperCase();
    const isEpin = productSubTypeUCase === SHARED_BROWSE_CONSTANTS.VOUCHER;
    return this.formBuilder.group({
      sku: this.formCreationService.getFormControl(
        FIELD_VALIDATIONS_CONSTANTS.SKU,
        validatedNonLineItem ? this.objectService.get(validatedNonLineItem, 'repEnteredSku') : '',
        !isEpin
      ),
    });
  }

  /**
   * This Method is used to build validation for each non Line Item
   * @param {CartNonLineItem} nonLineItem
   * @returns {FormGroup}
   */
  private buildDeviceValidateForm(deviceItem: CartNonLineItem): UntypedFormGroup {
    const validatedNonLineItem = this.stateStoreUtils.getNonLineItems(deviceItem.id);
    // QC610103
    if (
      deviceItem.productSubtype &&
      deviceItem.productSubtype.toLowerCase() !== DEVICE_TYPE.SIM_CARD
    ) {
      return this.formBuilder.group({
        imei: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.IMEI,
          this.objectService.get(validatedNonLineItem, 'imei') || '',
          true
        ),
        sku: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.SKU,
          this.objectService.get(validatedNonLineItem, 'repEnteredSku') || '',
          true
        ),
      });
    } else {
      return this.formBuilder.group({
        sim: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.SIM,
          this.objectService.get(validatedNonLineItem, 'simKit.sim') || '',
          true
        ),
        sku: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.SKU,
          this.objectService.get(validatedNonLineItem, 'repEnteredSku') || '',
          true
        ),
      });
    }
  }

  /**
   * This Method is used to build validation form for exchange Item
   * @param {ReplacementItem} deviceItem
   * @returns {FormArray}
   */
  private buildExchangeDeviceValidateForm(deviceItem: ReplacementItem): UntypedFormArray {
    const exchangeDeviceFormArray = this.formBuilder.array([]);
    if (deviceItem && Object.keys(deviceItem).length > 0) {
      const exchangeDeviceForm = this.formBuilder.group({
        imei: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.IMEI,
          this.objectService.get(deviceItem, 'imei') || '',
          true
        ),
        sku: this.formCreationService.getFormControl(
          FIELD_VALIDATIONS_CONSTANTS.SKU,
          this.objectService.get(deviceItem, 'repEnteredSku') || '',
          true
        ),
      });
      exchangeDeviceFormArray.push(exchangeDeviceForm);
    }
    return exchangeDeviceFormArray;
  }

  /**
   * method to create NonLineItems Data For Remorse Exchange
   * @param item of CartItem type
   * @param isDevice boolean
   * @param sku string
   * @return CartNonLineItem[]
   */
  private buildNonLineItemsDataForExchange(
    item: CartItem[],
    isDevice: boolean,
    sku?: string
  ): CartNonLineItem[] {
    const nonLineItems = [];
    if (item && item.length > 0) {
      const filteredItems = item.filter((itemUnit: CartItem) => {
        if (isDevice) {
          return itemUnit.productType[0].toUpperCase() === CART_LANDING_CONSTANTS.DEVICES;
        } else {
          return itemUnit.productType[0].toUpperCase() === CART_LANDING_CONSTANTS.ACCESSORIES;
        }
      });
      filteredItems.forEach((value: CartItem, index: number) => {
        const validatedNonLineItem = isDevice
          ? this.stateStoreUtils.getNonLineItems(this.objectService.get(item, `${index}.id`))
          : this.stateStoreUtils.getNonLineItems(this.objectService.get(item, `${index}.offerId`));

        nonLineItems.push({
          id: this.objectService.get(value, `id`),
          offerId: this.objectService.get(value, `offerId`),
          title: this.objectService.get(value, `offerName`),
          defaultSku: this.objectService.get(value, `sku`),
          repEnteredSku: this.objectService.get(validatedNonLineItem, 'repEnteredSku', ''),
          upc: this.objectService.get(value, 'upc'),
          productSubtype: this.objectService.get(value, 'productSubType.0')
            ? this.objectService.get(value, 'productSubType.0').toLowerCase()
            : '',
          tacCode: this.objectService.get(value, 'tacCode'),
        });
        if (isDevice) {
          nonLineItems[index].imei = this.objectService.get(value, 'imei');
          if (!!this.objectService.get(nonLineItems, `${index}.repEnteredSku`)) {
            this.updateCheckInventoryMap(
              index,
              VALIDATE_MODAL_CONSTANTS.NON_LINE_DEVICES_ITEMS,
              VALIDATE_MODAL_CONSTANTS.SKU,
              nonLineItems[index].repEnteredSku,
              nonLineItems[index].id
            );
          }
        }
      });
    }
    return nonLineItems;
  }
  /**
   * method to check if replacement item is available
   * @param cartResponse
   * @return boolean
   */
  public isStandAloneReturningItemAvailable(cartResponse: Cart): boolean {
    const hasOneStandAloneReturningItem = !!this.objectService.get(
      cartResponse,
      `returningItem.length`
    );
    return hasOneStandAloneReturningItem;
  }

  /**
   *
   * @param productType LINED_DEVICE / DEVICE/ SIM / ACCESSORY
   * @param inventoryCode SR305/ SR515/ SR504
   * @param isManagerOverride boolean true when override successfull
   * @param item Cart Item
   */
  public setRISvalidatedItems(
    productType: string,
    inventoryCode: string,
    isManagerOverride?: boolean,
    item?: CartLineItem,
    nonLineItem?: CartNonLineItem,
    managerId?: string
  ): RisInventoryDetails {
    const risValidatedItem: RisInventoryDetails = {};
    this.objectService.set(risValidatedItem, 'isSOAMigratedStore.isStoreRIS', true);
    switch (productType) {
      case VALIDATE_MODAL_CONSTANTS.LINED_DEVICE:
        if (
          inventoryCode === VALIDATE_MODAL_CONSTANTS.SELLABLECODE ||
          inventoryCode === VALIDATE_MODAL_CONSTANTS.NON_SELLABLE_OVERRIDE
        ) {
          this.objectService.set(risValidatedItem, 'risDetails.imeiScanned', 'manual');
          this.objectService.set(risValidatedItem, 'risDetails.skuScanned', 'manual');
          this.checkManagerOverrideForRIS(isManagerOverride, risValidatedItem, managerId);
        }
        break;
      case VALIDATE_MODAL_CONSTANTS.DEVICE:
        if (
          inventoryCode === VALIDATE_MODAL_CONSTANTS.SELLABLECODE ||
          inventoryCode === VALIDATE_MODAL_CONSTANTS.NON_SELLABLE_OVERRIDE
        ) {
          this.objectService.set(risValidatedItem, 'risDetails.imeiScanned', 'manual');
          this.objectService.set(risValidatedItem, 'risDetails.skuScanned', 'manual');
          this.checkManagerOverrideForRIS(isManagerOverride, risValidatedItem, managerId);
        }
        break;
      case VALIDATE_MODAL_CONSTANTS.SIM:
        const isSimSerlization = this.stateStoreUtils.getSimSerialization();
        if (item) {
          this.objectService.set(risValidatedItem, 'risDetails.simScanned', 'manual');
          this.objectService.set(risValidatedItem, 'risDetails.skuScanned', 'manual');
        } else {
          this.objectService.set(risValidatedItem, 'risDetails.simScanned', 'manual');
          this.objectService.set(risValidatedItem, 'risDetails.skuScanned', 'manual');
        }
        if (
          isSimSerlization === VALIDATE_MODAL_CONSTANTS.SIM_SERIALIZATION_ON &&
          (inventoryCode === VALIDATE_MODAL_CONSTANTS.SELLABLE_NOT_IN_INVENTORY ||
            inventoryCode === VALIDATE_MODAL_CONSTANTS.NON_SELLABLE_OVERRIDE)
        ) {
          this.objectService.set(risValidatedItem, 'risDetails.isReceive', true);
        } else {
          this.objectService.set(risValidatedItem, 'risDetails.isReceive', false);
        }
        break;
      case VALIDATE_MODAL_CONSTANTS.ACCESSORY:
        this.objectService.set(risValidatedItem, 'risDetails.skuScanned', 'manual');
        this.objectService.set(risValidatedItem, 'risDetails.isReceive', false);
        break;
      default:
        break;
    }
    return risValidatedItem;
  }
  /**
   * method to check ManagerOverrideForRIS is available
   * @param isManagerOverride
   * @param risValidatedItem
   * @param managerId
   * @return boolean
   */
  private checkManagerOverrideForRIS(
    isManagerOverride?: boolean,
    risValidatedItem?: RisInventoryDetails,
    managerId?: string
  ): void {
    if (isManagerOverride) {
      this.objectService.set(risValidatedItem, 'risDetails.isReceive', true);
      this.objectService.set(risValidatedItem, 'risDetails.managerId', managerId);
    } else {
      this.objectService.set(risValidatedItem, 'risDetails.isReceive', false);
    }
  }
}
